import { IconButton, Layout, Text, TextLink } from '@oneaudi/unified-web-components';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { FsagMonthlyPriceRateDialog } from '~/app/compositions/content/calculation-info/components/fsag-finance-dialog';
import PriceFootnote from '~/app/compositions/content/calculation-info/components/price-footnote';
import {
	CalculationInfoStyle,
	MonthlyPrice,
	PriceBarTotalPrice,
	TotalPrice,
	TotalPricePrefix,
	TotalPriceSection,
} from '~/app/compositions/content/calculation-info/styles/calculation-info-style';
import { useAppServices } from '~/app/contexts/app-context';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useCtxConfigurator } from '~/app/pages/configurator/context/ctx-configurator';
import { PriceDetailsFsagDialog } from '~/app/pages/summary/dialogs/price-detail/';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { useCarlineQuery } from '~/graphql/hooks/use-configured-car-queries';
import { useBreakPoint } from '~/hooks/responsive/useBreakPoint';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import { BasicProps } from '~/models/basic-props';
import { state } from '~/storage';
import { DATA_TEST_ID } from '~/test/data-test-id';

export function CalculationInfoLegalGB({ className }: BasicProps) {
	const { config, formatter } = useMarketConfig();
	const { loadingFlags, catalog, carIdentifier } = useConfigurator();
	const { formatPriceWithVAT } = useCurrencyFormatter();
	const showLoadingIndicator = loadingFlags.priceSummary;
	const { i18n } = useI18n();
	const { isMBreakpoint, isXlBreakpoint } = useBreakPoint();
	const totalPrice = catalog.priceSummary?.totalPrice;
	const monthlyRate = catalog.priceSummary?.monthlyRate;
	const currentView = useAtomValue(state.currentView);
	const {
		featureServices: { footnoteService },
	} = useAppServices();
	footnoteService?.getData();
	const carlineData = useCarlineQuery(carIdentifier, config.financingInput);

	const carType =
		carlineData?.carline?.configuredCar?.carlineStructureCarline?.vehicleType ||
		carlineData?.carline?.configuredCar?.technicalDataV2?.modelAttributes?.driveTrain;
	const priceDisclaimerText = useMemo(() => {
		if (config.enablePriceDisclaimer) {
			return carType
				? carType === 'BEV'
					? i18n('ngc.remarks.price.legalBEV')
					: i18n('ngc.remarks.price.legalICE')
				: undefined;
		}
		return null;
	}, [config.enablePriceDisclaimer, carType, i18n]);

	const { openDialog, isDialogOpen, closeDialog } = useDialogs();
	const { setOpenPriceDetails } = useCtxConfigurator();

	const trackClickEvent = useCallback((eventName: string, label: string) => {
		appTrackingService.summaryClickEvent({
			eventAction: EventAction.InternalLink,
			eventName: `configurator (price bar) - click to open ${eventName}`,
			componentName: 'configurator-price-bar',
			label,
		});
	}, []);

	return (
		<CalculationInfoStyle className={className}>
			{showLoadingIndicator ? (
				<Text
					tag={'p'}
					variant={{ type: 'copy', order: '1', style: 'bold' }}
					className="calculation-info-item loading">
					{i18n('ngc.currency.calculatingPrice')}...
				</Text>
			) : (
				<>
					<div
						data-testid={DATA_TEST_ID.TOTAL_PRICE_CONTAINER}
						className={`calculation-info total-price ${
							currentView === 'configurator' && isMBreakpoint ? 'footer-show-price-container' : ''
						}`}>
						{currentView === 'summary' ? (
							<PriceBarTotalPrice>
								<Text
									tag={'p'}
									variant={{ type: 'copy', order: '2', style: 'normal' }}
									className="calculation-info-item">
									{i18n('ngc.currency.withVAT')}
								</Text>
								<Layout>
									<TotalPrice>{formatter.formatPrice(totalPrice?.value)}</TotalPrice>
									<Text
										tag={'p'}
										variant={{ type: 'copy', order: '1', style: 'bold' }}
										className="calculation-info-item">
										{config.enablePriceDisclaimer && <PriceFootnote text={priceDisclaimerText} />}
									</Text>
								</Layout>
							</PriceBarTotalPrice>
						) : (
							<TotalPriceSection>
								<TotalPricePrefix>{i18n('ngc.priceDetails.totalPrice')}</TotalPricePrefix>
								<TotalPrice>
									{formatPriceWithVAT(totalPrice)}
									{config.enablePriceDisclaimer && <PriceFootnote text={priceDisclaimerText} />}{' '}
								</TotalPrice>
							</TotalPriceSection>
						)}
						{isMBreakpoint && currentView === 'configurator' ? (
							<IconButton
								label={''}
								size={'sm'}
								onClick={() => {
									setOpenPriceDetails(true);
									trackClickEvent('price details layer', i18n('ngc.actions.showPriceDetails'));
								}}
								variant={'tertiary'}
								icon={'arrow-up'}
								aria-label={i18n('ngc.actions.showPriceDetails')}
							/>
						) : (
							<TextLink
								className="text-grey"
								onClick={() => {
									openDialog('priceDetails');
									trackClickEvent('price details layer', i18n('ngc.actions.showPriceDetails'));
								}}
								size={'sm'}
								variant={'standalone'}>
								{i18n('ngc.actions.showPriceDetails')}
							</TextLink>
						)}

						<PriceDetailsFsagDialog
							open={isDialogOpen('priceDetails')}
							onClose={() => closeDialog('priceDetails')}
						/>
					</div>
					{monthlyRate?.__typename === 'MonthlyRate' && !isXlBreakpoint && config.enableFinancingServices && (
						<div className="calculation-info monthly-price">
							<MonthlyPrice>
								<TotalPricePrefix>Click below to explore financing options</TotalPricePrefix>
							</MonthlyPrice>

							<TextLink
								className="text-grey"
								onClick={() => {
									openDialog('monthlyPriceRatesFsag');
									trackClickEvent('financing options layer', i18n('ngc.actions.changeLeasingRate'));
								}}
								size={'sm'}
								variant={'standalone'}>
								{i18n('ngc.actions.changeLeasingRate')}
							</TextLink>
						</div>
					)}
					{monthlyRate?.__typename === 'MonthlyRate' && config.enableFinancingServices && (
						<div>
							<FsagMonthlyPriceRateDialog
								open={isDialogOpen('monthlyPriceRatesFsag')}
								onClose={() => closeDialog('monthlyPriceRatesFsag')}
							/>
						</div>
					)}
				</>
			)}
		</CalculationInfoStyle>
	);
}
